// VehicleFuelChart component - Chart for fuel data by vehicle

import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import {
  formatDateShort,
  formatCurrency,
  formatNumber,
  formatDecimal,
} from "../../utils/formatters";

const VehicleFuelChart = ({ vehicleFuelData }) => {
  const [chartType, setChartType] = useState("amount"); // amount, volume, count

  // Group data by license plate and sum values
  const aggregatedData = vehicleFuelData.reduce((acc, record) => {
    const plate = record.license_plate;
    if (!acc[plate]) {
      acc[plate] = {
        license_plate: plate,
        total_amount: 0,
        total_volume: 0,
        fill_count: 0,
        avg_price: 0,
        dates: [],
      };
    }
    acc[plate].total_amount += record.total_amount || 0;
    acc[plate].total_volume += record.total_volume || 0;
    acc[plate].fill_count += record.fill_count || 0;
    acc[plate].avg_price = record.avg_price || 0;
    acc[plate].dates.push(record.date);
    return acc;
  }, {});

  // Convert to array and sort by license plate
  const chartData = Object.values(aggregatedData)
    .sort((a, b) => a.license_plate.localeCompare(b.license_plate))
    .map((item) => ({
      ...item,
      avg_amount_per_fill: item.total_amount / item.fill_count,
    }));

  // Get chart config based on type
  const getChartConfig = () => {
    switch (chartType) {
      case "amount":
        return {
          dataKey: "total_amount",
          name: "จำนวนเงิน",
          color: "#f97316",
          unit: "บาท",
        };
      case "volume":
        return {
          dataKey: "total_volume",
          name: "ปริมาณ",
          color: "#3b82f6",
          unit: "ลิตร",
        };
      case "count":
        return {
          dataKey: "fill_count",
          name: "จำนวนครั้ง",
          color: "#10b981",
          unit: "ครั้ง",
        };
      default:
        return {
          dataKey: "total_amount",
          name: "จำนวนเงิน",
          color: "#f97316",
          unit: "บาท",
        };
    }
  };

  const config = getChartConfig();

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border-2 border-gray-300 rounded-lg shadow-xl">
          <p className="text-sm font-bold text-gray-900 mb-2">
            รถทะเบียน: {data.license_plate}
          </p>
          <div className="space-y-1">
            <p className="text-sm text-orange-600 font-semibold">
              จำนวนเงิน: {formatCurrency(data.total_amount)}
            </p>
            <p className="text-sm text-blue-600 font-semibold">
              ปริมาณ: {formatDecimal(data.total_volume)} ลิตร
            </p>
            <p className="text-sm text-green-600 font-semibold">
              จำนวนครั้ง: {data.fill_count} ครั้ง
            </p>
            <p className="text-sm text-purple-600 font-semibold">
              ราคาเฉลี่ย: {formatDecimal(data.avg_price)} บาท/ลิตร
            </p>
            <p className="text-sm text-indigo-600 font-semibold">
              เฉลี่ยต่อครั้ง: {formatCurrency(data.avg_amount_per_fill)}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!vehicleFuelData || vehicleFuelData.length === 0) {
    return (
      <div className="bg-white rounded-xl p-8 text-center border-2 border-gray-200">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path
                fillRule="evenodd"
                d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <p className="text-gray-600 font-medium">
            ไม่มีข้อมูลการเติมน้ำมันตามรถ
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Chart Type Toggle */}
      <div className="flex flex-wrap gap-3 justify-center">
        <button
          onClick={() => setChartType("amount")}
          className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
            chartType === "amount"
              ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg transform scale-105"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          <span className="flex items-center gap-2">💰 จำนวนเงิน</span>
        </button>
        <button
          onClick={() => setChartType("volume")}
          className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
            chartType === "volume"
              ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-105"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          <span className="flex items-center gap-2">⛽ ปริมาณน้ำมัน</span>
        </button>
        <button
          onClick={() => setChartType("count")}
          className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
            chartType === "count"
              ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg transform scale-105"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          <span className="flex items-center gap-2">📋 จำนวนครั้ง</span>
        </button>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
        <h5 className="text-lg font-bold text-gray-800 mb-4">
          {config.name}แยกตามรถ
        </h5>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="license_plate"
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fill: "#4b5563", fontSize: 12 }}
            />
            <YAxis tick={{ fill: "#4b5563", fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey={config.dataKey}
              fill={config.color}
              radius={[8, 8, 0, 0]}
              animationDuration={1000}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default VehicleFuelChart;
